import {useMemo} from 'react';
import {useParams} from 'react-router-dom';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {IconButton, Tooltip} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  BoundExternalParticipant,
  useGetBoundExternalParticipants,
  useGetParticipant,
  useUnbindExternalParticipant,
} from '@entities/participant';
import {CardBox, MrTable} from '@shared/components';
import {entityTableCommonProps} from '@shared/constants';
import {BaseWidgetLayout, Header} from '@widgets/common';
import {EditParticipantForm} from '@widgets/participant/EditParticipantForm';

import {COLOR__WHITE} from '@/theme/colors';

const ExternalParticipantsTable = ({
  participantId,
}: {
  participantId: string;
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {data, isPending} = useGetBoundExternalParticipants(participantId);
  const {mutateAsync: unbind, isPending: isUnbinding} =
    useUnbindExternalParticipant(participantId);

  const columns = useMemo<MRT_ColumnDef<BoundExternalParticipant>[]>(
    () => [
      {
        accessorKey: 'external_id',
        header: 'External ID',
        size: 240,
      },
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'subcategory_slug',
        header: 'Subcategory',
        size: 160,
      },
      {
        accessorKey: 'id',
        header: 'Record ID',
        size: 300,
      },
    ],
    [],
  );

  return (
    <MrTable
      columns={columns}
      data={data ?? []}
      {...entityTableCommonProps}
      enableRowActions
      positionActionsColumn="last"
      renderRowActions={({row}) => (
        <Tooltip title="Unbind">
          <span>
            <IconButton
              size="small"
              disabled={isUnbinding}
              onClick={async () => {
                try {
                  await unbind(row.original.id);
                  enqueueSnackbar('Unbound successfully', {variant: 'success'});
                } catch {
                  enqueueSnackbar('Failed to unbind', {variant: 'error'});
                }
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      state={{isLoading: isPending}}
    />
  );
};

export const EditParticipantPage = () => {
  const {participant_id} = useParams<{participant_id: string}>();

  const {data, isPending, error} = useGetParticipant(participant_id ?? '', {
    enabled: !!participant_id,
  });

  if (!participant_id) {
    return 'Invalid participant ID';
  }

  return (
    <>
      <Header title={`Participant ${participant_id}`} mb={1} />

      <BaseWidgetLayout isPending={isPending} error={error}>
        {data && (
          <>
            <CardBox bgcolor={COLOR__WHITE} mb={3}>
              <EditParticipantForm participant={data} />
            </CardBox>

            <CardBox bgcolor={COLOR__WHITE}>
              <Header
                title="External participants"
                mb={2}
                isBackButtonShown={false}
              />
              <ExternalParticipantsTable participantId={participant_id} />
            </CardBox>
          </>
        )}
      </BaseWidgetLayout>
    </>
  );
};
